import React, { useEffect, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { TYPE_CONFIG } from './NotificationItem';

const { FiX } = FiIcons;

const PRIORITY_GLOW = {
  urgent: 'shadow-[0_0_24px_rgba(239,68,68,0.3)] border-red-500/25',
  high:   'shadow-[0_0_20px_rgba(255,102,204,0.25)] border-brand-pink/25',
  normal: 'shadow-[0_8px_32px_rgba(0,0,0,0.5)] border-white/8',
  low:    'shadow-[0_4px_16px_rgba(0,0,0,0.4)] border-white/5',
};

const BAR_GRADIENT = {
  urgent: 'from-red-500 to-orange-500',
  high:   'from-brand-pink to-brand-coral',
  normal: 'from-brand-pink to-brand-coral',
  low:    'from-zinc-600 to-zinc-500',
};

const UnlockToast = ({ toast, onDismiss }) => {
  const [visible, setVisible]   = useState(false);
  const [progress, setProgress] = useState(100);
  const cfg = TYPE_CONFIG[toast.type] || TYPE_CONFIG.unlock;
  const priority = toast.priority || 'normal';
  const DURATION = priority === 'urgent' ? 8000 : priority === 'high' ? 6000 : 5000;

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
    const start = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - start;
      setProgress(Math.max(0, 100 - (elapsed / DURATION) * 100));
    }, 50);
    const timer = setTimeout(() => {
      setVisible(false);
      setTimeout(() => onDismiss(toast.id), 350);
    }, DURATION);
    return () => { clearTimeout(timer); clearInterval(interval); };
  }, [toast.id, onDismiss, DURATION]);

  const dismiss = () => { setVisible(false); setTimeout(() => onDismiss(toast.id), 350); };

  return (
    <div className={`relative w-[340px] rounded-3xl overflow-hidden transition-all duration-350 border backdrop-blur-2xl ${
      PRIORITY_GLOW[priority]
    } ${visible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-6 scale-95'}`}
      style={{
        background: 'linear-gradient(160deg, rgba(25,16,38,0.97) 0%, rgba(15,10,25,0.98) 100%)',
      }}>

      {/* Progress bar */}
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-white/5">
        <div className={`h-full bg-gradient-to-r ${BAR_GRADIENT[priority]} transition-all duration-100`}
          style={{ width: `${progress}%` }} />
      </div>

      <div className="flex items-start gap-3.5 p-5 pt-6">
        {/* Icon */}
        <div className={`w-10 h-10 rounded-2xl border flex items-center justify-center flex-shrink-0 ${cfg.bg}`}>
          <SafeIcon icon={cfg.icon} className={`text-base ${cfg.color}`} />
        </div>

        <div className="flex-1 min-w-0 space-y-1">
          <div className="flex items-center gap-2">
            <span className={`text-[9px] font-black uppercase tracking-widest ${cfg.color}`}>{cfg.label}</span>
            {priority === 'urgent' && (
              <span className="text-[9px] font-black uppercase tracking-widest text-red-400 px-1.5 py-0.5 rounded-full bg-red-500/15 border border-red-500/25">
                Urgent
              </span>
            )}
            {priority === 'high' && (
              <span className="text-[9px] font-black uppercase tracking-widest text-brand-pink px-1.5 py-0.5 rounded-full bg-brand-pink/10 border border-brand-pink/20">
                High
              </span>
            )}
          </div>
          <p className="text-sm font-black text-white leading-tight">{toast.title}</p>
          <p className="text-[11px] font-bold text-zinc-400 leading-relaxed">{toast.message}</p>
          {toast.metadata?.amount && (
            <p className="text-[9px] font-black text-green-400 uppercase tracking-widest pt-0.5">
              ${toast.metadata.amount} paid to artist
            </p>
          )}
        </div>

        <button onClick={dismiss}
          className="text-zinc-700 hover:text-white transition-colors flex-shrink-0 mt-0.5 p-1 rounded-lg hover:bg-white/5">
          <SafeIcon icon={FiX} className="text-sm" />
        </button>
      </div>
    </div>
  );
};

export default UnlockToast;